import { useTranslation } from "react-i18next";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { useContentAndProgress } from "../../../../../contexts/UserProgressContext";
import { useContentActions } from "../../../../../hooks/useContentActions";
import ProgressBar from "../../../../../components/progressBar";
import { useSkipMilestone } from "../../../../../hooks/useSkipMilestone";
import { CircularProgress } from "@mui/material";
import ArrowLeft from "../../../../../assets/arrows-left.svg"; // Import left arrow SVG
import ArrowRight from "../../../../../assets/arrows-right.svg"; //
import { ReactComponent as CheckMarkDone } from "../../../../../assets/done_check_active.svg";
import { ReactComponent as DoneInactive } from "../../../../../assets/done_check_white.svg";
import { useEffect, useRef, useState } from "react";
import PhaseListModal from "../../components/PhaseListModal";
import { useMilestoneNavigation } from "../../../../../hooks/useMilestoneNavigation";
import PhaseChangeModal from "../../components/PhaseChangeModal";
import { IContentWithProgress } from "../../../../../@types";

export function MilestoneHeader() {
  const { t } = useTranslation("home");
  // State to handle touch movements
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState("right"); 
  const {
    activeMilestoneContent,
    activeMilestone,
    currentPhaseMilestones,
    setActiveMilestone,
    setOpenedContent,
    activePhase,
    journey,
    phases,
  } = useContentAndProgress();
  const { handleDoneClick, handleRollbackDoneClick } = useContentActions({
    content: activeMilestoneContent,
  });

  const handleGoToPhaseAndMilestoneListView = () => {
    setIsModalOpen(true);
  };


  const {
    goToNextMilestone,
    goToPreviousMilestone,
    isPhaseChangeModalOpen,
    closeModal,
  } = useMilestoneNavigation();

  const relevantMilestones = currentPhaseMilestones?.filter(
    (milestone: IContentWithProgress) => !milestone.markedIrrelevant
  );

  const activeMilestoneVals = relevantMilestones?.find(
    (cpm) => cpm.id === activeMilestone?.id
  );

  // Start Calculate phase and milestone position
  const journeyChildsIds = journey?.content?.childContentIds;
  const phaseChildsIds = activePhase?.content?.childContentIds;

  const activePhaseId = activePhase?.content?.platformContentId;
  const activeMilestoneId = activeMilestone?.content?.platformContentId;

  let milestonePosition;

  if (relevantMilestones && activeMilestoneId) {
    milestonePosition =
      relevantMilestones.findIndex(
        (ms: IContentWithProgress) => ms.content.platformContentId === activeMilestoneId
      ) + 1;
  }

  let phasePosition;
  if (journeyChildsIds && activePhaseId) {
    phasePosition = journeyChildsIds.indexOf(activePhaseId) + 1;
  }
  // End Calculate phase and milestone position

  const skipMilestone = useSkipMilestone();

    // Function to handle milestone change
    const handleMilestoneChange = () => {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 500); // Reset after 0.5s
    };
  
    useEffect(() => {
      handleMilestoneChange();
    }, [activeMilestone])

    
  if (!currentPhaseMilestones) {
    // Render loading state while coachDetails are being fetched
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress style={{ color: "#DCAA00" }} />
      </div>
    );
  }

  const currentMilestoneIndex = currentPhaseMilestones.findIndex(
    (ms) => ms.id === activeMilestone?.id
  );
  const currentPhaseIndex = phases?.findIndex((p) => p.id === activePhase?.id);
  
  // Check if the current milestone is the first in the current phase
  const isFirstMilestoneInPhase = currentMilestoneIndex === 0;
  // Check if the current milestone is the last in the current phase
  const isLastMilestoneInPhase =
    currentMilestoneIndex === currentPhaseMilestones.length - 1;

  // Check if the current phase is the first one
  const isFirstPhase = currentPhaseIndex === 0;
  // Check if the current phase is the last one
  const isLastPhase = phases ? currentPhaseIndex === phases.length - 1 : false;

  // Check if we are at the first milestone of the first phase
  const isFirstMilestoneOverall = isFirstMilestoneInPhase && isFirstPhase;
  // Check if we are at the last milestone of the last phase
  const isLastMilestoneOverall = isLastMilestoneInPhase && isLastPhase;

  const handleMarkDoneAndMoveToNextMilestone = (e: any) => {
    handleDoneClick(e);
    goToNextMilestone();
    window.scrollTo(0, 0);
  };

  
  // Function to handle touch start
  const handleTouchStart = (e: any) => {
    touchStartX.current = e.touches[0].clientX; 
  };

  // Function to handle touch move
  const handleTouchMove = (e: any) => {
    touchEndX.current = e.touches[0].clientX;
  };

  // Function to handle touch end
  const handleTouchEnd = () => {
    // Calculate the distance swiped
    const distanceX = touchStartX.current - touchEndX.current;
    // If swiped left, go to next milestone
    if (distanceX > 50 && touchEndX.current !== 0) {
      goToNextMilestone();
    }

    // If swiped right, go to previous milestone
    else if (distanceX < -50) {
      goToPreviousMilestone();
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };



  return (
    <>
      {activeMilestone ? (
        <>
          {
            <PhaseChangeModal
              isOpen={isPhaseChangeModalOpen}
              onClose={closeModal}
            />
          }
          {/* Modal Implementation */}
          {isModalOpen && (
            <PhaseListModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          )}

          <div className="relative">
            {/* Left Arrow */}

            <div className="bg-Blue-metalic lg:p-6 px-4 py-2 rounded-lg lg:mb-6 mb-2 min-w-full">
              {!isFirstMilestoneOverall && (
                <div
                  onClick={goToPreviousMilestone}
                  className="absolute left-0 top-[50%] transform -translate-y-[50%] -translate-x-6 cursor-pointer lg:hidden flex items-center justify-center w-[26px] h-[26px]"
                >
                  <img
                    src={ArrowLeft}
                    alt="Previous"
                    className="w-[12px] h-[12px]"
                  />
                </div>
              )}
              <div
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`${isAnimating ? 'transition-opacity duration-500 opacity-20' : 'opacity-100'}`}
              >
                {phasePosition && (
                  <div className="lg:hidden flex font-[500] text-[12px] leading-5">
                    Phase {phasePosition}, {t("milestone")} {milestonePosition}
                  </div>
                )}
                <h1 className="text-xl font-semibold mb-4 hidden lg:flex">
                  {activeMilestoneContent?.title}
                </h1>
                <h1 className=" font-semibold lg:hidden flex mb-1 font-[700] text-[16px] leading-4">
                  {activeMilestoneContent?.title}
                </h1>
              </div>
              {/* {!activeMilestoneVals?.markedIrrelevant ?
                            <div className="lg:hidden flex justify-between items-center mb-2">
                                <ProgressBar percentage={Math.round(activeMilestoneVals?.progressValue || 0)} progressColor={ "bg-black"} />
                                <div className="ml-2 font-bold text-sm">{Math.round(activeMilestoneVals?.progressValue || 0)}%</div>
                            </div>
                            : null} */}

              <div className={`${isAnimating ? 'transition-opacity duration-500 opacity-20' : 'opacity-100'} lg:block w-full h-[1px] bg-white lg:mb-4 mb-1 mt-2 `}></div>
              <div className="flex">
                {activeMilestone.markedIrrelevant ? null : (
                  <>
                    {activeMilestone?.progressValue !== 100 ? (
                      <div className="lg:flex w-full justify-end">
                        <div className="hidden lg:flex w-full justify-center lg:w-fit">
                          <Button
                            className="mr-3 bg-transparent border-0 w-full"
                            onClick={() => skipMilestone()}
                            variant={ButtonVariant.GHOST_DARK}
                            size={ButtonSize.SMALL}
                          >
                            <span className="font-medium">{t("skip")}</span>
                          </Button>
                        </div>
                        <div className="hidden lg:flex w-full justify-center lg:w-fit">
                          <Button
                            className="w-full"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            onClick={handleDoneClick}
                          >
                            <span className="font-medium">{t("next")}</span>
                          </Button>
                        </div>

                        <div className={`${isAnimating ? 'transition-opacity duration-500 opacity-20' : 'opacity-100'} lg:hidden flex w-full justify-between items-center`}>
                          <div
                            className="cursor-pointer font-[500] text-[12px] leading-6"
                            onClick={handleGoToPhaseAndMilestoneListView}
                          >
                            {t("milestone-header-goto-list")} &gt;
                          </div>

                          {/* <DoneInactive
                            onClick={handleMarkDoneAndMoveToNextMilestone}
                            className="cursor-pointer -mr-1"
                          /> */}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="hidden lg:flex w-full justify-end">
                          <Button
                            onClick={handleRollbackDoneClick}
                            className="bg-Grey-light-2 px-4 py-1.5 text-Black"
                          >
                            <span className="font-medium">
                              {t("concluded")}
                            </span>
                          </Button>
                        </div>

                        <div className={`${isAnimating ? 'transition-opacity duration-500 opacity-20' : 'opacity-100'} lg:hidden flex w-full justify-between items-center `}>
                          <div
                            className="cursor-pointer font-[500] text-[12px] leading-6"
                            onClick={handleGoToPhaseAndMilestoneListView}
                          >
                            {t("milestone-header-goto-list")} &gt;
                          </div>

                          {/* <CheckMarkDone
                            onClick={handleRollbackDoneClick}
                            className="cursor-pointer -mr-1"
                          /> */}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {!isLastMilestoneOverall && (
                <div
                  onClick={goToNextMilestone}
                  className="absolute right-0 top-[50%] transform -translate-y-[50%] translate-x-6 cursor-pointer lg:hidden flex items-center justify-center w-[26px] h-[26px]"
                >
                  <img
                    src={ArrowRight}
                    alt="Next"
                    className="w-[12px] h-[12px]"
                  />
                </div>
              )}
            </div>
            <h2 className=" hidden lg:flex font-semibold text-xl text-Yellow-default mb-4">
              {t("subtitle")}
            </h2>
            <div className=" lg:hidden mb-4"></div>
          </div>
        </>
      ) : null}
    </>
  );
}
